import { Component, computed, input, output, signal, effect } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { RadioButtonModule } from "primeng/radiobutton";
import { InputTextModule } from "primeng/inputtext";
import { SelectModule } from "primeng/select";
import { MultiSelectModule } from "primeng/multiselect";
import { TooltipModule } from "primeng/tooltip";
import { TextareaModule } from "primeng/textarea";
import { ButtonModule } from "primeng/button";
import { DialogModule } from "primeng/dialog";

export type ModerationAction = "ban" | "suspend" | "active" | "review" | "deactivate";

export interface ModerationActionPayload {
  action: ModerationAction;
  reason?: string;
  emailSubject?: string;
  emailBody?: string;
  suspendDays?: number;
  suspendedServices?: string[];
  sendEmail?: boolean;
}

@Component({
  selector: "app-moderation-action-form",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RadioButtonModule,
    InputTextModule,
    SelectModule,
    MultiSelectModule,
    TooltipModule,
    TextareaModule,
    ButtonModule,
    DialogModule
  ],
  templateUrl: "./moderation-action-form.component.html",
  styleUrl: "./moderation-action-form.component.css"
})
export class ModerationActionFormComponent {
  accountStatus = input<'active' | 'suspended' | 'banned'>('active');
  isReport = input<boolean>(false);
  userName = input<string>('User');
  userActive = input<boolean>(true);
  reportStatus = input<string>('pending');

  onAction = output<ModerationActionPayload>();
  onCancel = output<void>();

  reasonOptions = [
    { label: "Spam", value: "Spam" },
    { label: "Harassment", value: "Harassment" },
    { label: "Bullying", value: "Bullying" },
    { label: "Impersonation", value: "Impersonation" },
    { label: "Hate speech", value: "Hate speech" },
    { label: "Nudity or sexual content", value: "Nudity or sexual content" },
    { label: "Underage user", value: "Underage user" },
    { label: "Other", value: "Other" }
  ];

  serviceOptions = [
    { label: "Swipe", value: "swipe" },
    { label: "Chat", value: "chat" },
    { label: "Lobby Creation", value: "lobby" },
    { label: "Account", value: "account" }
  ];

  selectedAction = signal<ModerationAction | null>(null);

  // Active (Unban/Unsuspend)
  activeReason = signal<string>('');

  // Ban
  banReason = signal<string | null>(null);
  sendBanEmail = signal<boolean>(true);
  banEmailSubject = signal<string>("Your account has been banned");
  banEmailBody = signal<string>(
    "Your account has been permanently banned for violating our community guidelines. If you believe this is a mistake, contact support."
  );

  // Suspend
  suspendReason = signal<string | null>(null);
  suspendDays = signal<number | null>(null);
  suspendedServices = signal<string[]>([]);
  sendSuspendEmail = signal<boolean>(true);
  suspendEmailSubject = signal<string>("Your account has been suspended");
  suspendEmailBody = signal<string>(
    "Your account has been temporarily suspended for violating our community guidelines. Access will be restored automatically once the suspension period ends."
  );

  // Review
  reviewEmailSubject = signal<string>("");
  reviewEmailBody = signal<string>("");

  showConfirm = signal<boolean>(false);

  private initializedForStatus = new Set<string>();

  constructor() {
    // Only auto-select a default action the first time each status is seen,
    // so it doesn't override the admin manually deselecting via toggleAction()
    effect(() => {
      const status = this.accountStatus();
      if (this.initializedForStatus.has(status)) return;
      this.initializedForStatus.add(status);

      if (status === 'suspended' || status === 'banned') {
        this.selectedAction.set('active');
      } else {
        this.selectedAction.set(null);
      }
    }, { allowSignalWrites: true });

    effect(() => {
      const days = this.suspendDays();
      const services = this.suspendedServices();
      let body = "Your account has been temporarily suspended for violating our community guidelines.";
      
      if (services && services.length > 0) {
        const servicesList = services.map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(', ');
        body += `\n\nRestricted services: ${servicesList}.`;
      }
      
      if (days) {
        body += `\n\nAccess will be restored automatically in ${days} day(s).`;
      } else {
        body += `\n\nAccess will be restored automatically once the suspension period ends.`;
      }
      this.suspendEmailBody.set(body);
    }, { allowSignalWrites: true });
  }

  availableActions = computed(() => {
    const status = this.accountStatus();
    const report = this.isReport();
    const actions: { id: ModerationAction; label: string; tooltip: string }[] = [];

    if (status === 'suspended') {
      actions.push({ id: 'active', label: 'Unsuspend', tooltip: 'Restore user to active status' });
    } else if (status === 'banned') {
      actions.push({ id: 'active', label: 'Unban', tooltip: 'Restore user to active status' });
    } else {
      // active
      const isActive = this.userActive();
      if (!isActive) {
        if (!report) actions.push({ id: 'active', label: 'Activate', tooltip: 'Activate user account' });
      } else {
        if (!report) actions.push({ id: 'deactivate', label: 'Deactivate', tooltip: 'Set user as inactive without banning' });
        actions.push({ id: 'suspend', label: 'Suspend', tooltip: 'Temporarily limits account access' });
        actions.push({ id: 'ban', label: 'Ban', tooltip: 'Permanently bans this user' });
      }
    }

    if (report && this.reportStatus() !== 'reviewed' && this.reportStatus() !== 'resolved' && this.reportStatus() !== 'banned' && this.reportStatus() !== 'suspended') {
      actions.push({ id: 'review', label: 'Mark as reviewed', tooltip: 'No violation' });
    }

    return actions;
  });

  canSubmit = computed(() => {
    const action = this.selectedAction();
    if (!action) return false;
    if (action === 'suspend') {
      if (!this.suspendDays()) return false;
      if (this.suspendedServices().length === 0) return false;
    }
    return true;
  });

  confirmTitle = computed(() => {
    switch (this.selectedAction()) {
      case "ban": return "Confirm ban";
      case "suspend": return "Confirm suspension";
      case "active": return "Confirm activation";
      case "deactivate": return "Confirm deactivation";
      case "review": return "Confirm mark as reviewed";
      default: return "";
    }
  });

  toggleAction(actionId: ModerationAction): void {
    this.selectedAction.set(this.selectedAction() === actionId ? null : actionId);
  }

  openConfirm(): void {
    if (!this.canSubmit()) return;
    this.showConfirm.set(true);
  }

  cancelConfirm(): void {
    this.showConfirm.set(false);
  }

  cancelForm(): void {
    this.selectedAction.set(null);

    this.activeReason.set('');

    this.banReason.set(null);
    this.sendBanEmail.set(true);
    this.banEmailSubject.set("Your account has been banned");
    this.banEmailBody.set(
      "Your account has been permanently banned for violating our community guidelines. If you believe this is a mistake, contact support."
    );

    this.suspendReason.set(null);
    this.suspendDays.set(null);
    this.suspendedServices.set([]);
    this.sendSuspendEmail.set(true);
    this.suspendEmailSubject.set("Your account has been suspended");
    this.suspendEmailBody.set(
      "Your account has been temporarily suspended for violating our community guidelines. Access will be restored automatically once the suspension period ends."
    );

    this.reviewEmailSubject.set('');
    this.reviewEmailBody.set('');

    this.onCancel.emit();
  }

  confirmAction(): void {
    const action = this.selectedAction();
    if (!action) return;

    let payload: ModerationActionPayload;

    if (action === "ban") {
      payload = {
        action,
        reason: this.banReason() ?? undefined,
        emailSubject: this.banEmailSubject(),
        emailBody: this.banEmailBody(),
        sendEmail: this.sendBanEmail()
      };
    } else if (action === "suspend") {
      payload = {
        action,
        reason: this.suspendReason() ?? undefined,
        emailSubject: this.suspendEmailSubject(),
        emailBody: this.suspendEmailBody(),
        suspendDays: this.suspendDays() ?? undefined,
        suspendedServices: this.suspendedServices(),
        sendEmail: this.sendSuspendEmail()
      };
    } else if (action === "active" || action === "deactivate") {
      payload = {
        action,
        reason: this.activeReason() || undefined,
        sendEmail: false
      };
    } else {
      payload = {
        action,
        emailSubject: this.reviewEmailSubject() || undefined,
        emailBody: this.reviewEmailBody() || undefined,
        sendEmail: !!this.reviewEmailSubject()
      };
    }

    this.onAction.emit(payload);
    this.showConfirm.set(false);
  }
}